/**
 * Theme colors from env (VITE_THEME_*).
 * Values are HSL triplets without "hsl()", e.g. "217 91% 34%".
 * When VITE_PRR_MODE is true, PRR identity defaults (verde/cinzento) are used
 * and can be overridden by any VITE_THEME_* set in env.
 */

const prrMode = import.meta.env.VITE_PRR_MODE === "true";

/** PRR identity: verde principal #369D1D, verde escuro #344923, verde claro #4EB856, cinzento #454142 */
const prrLight = {
  primary: "108 69% 36%",
  primaryForeground: "0 0% 100%",
  background: "340 14% 97%",
  foreground: "345 3% 26%",
  accent: "125 43% 51%",
  accentForeground: "0 0% 100%",
  secondary: "125 43% 51%",
  muted: "340 14% 93%",
  mutedForeground: "345 3% 46%",
  border: "345 10% 88%",
  ring: "108 69% 36%",
  sidebarBackground: "93 35% 21%",
  sidebarForeground: "0 0% 100%",
};

const prrDark = {
  primary: "125 43% 51%",
  primaryForeground: "345 3% 26%",
  background: "93 35% 12%",
  foreground: "340 14% 97%",
  accent: "108 69% 36%",
  accentForeground: "0 0% 100%",
  secondary: "108 69% 36%",
  muted: "93 35% 18%",
  mutedForeground: "340 14% 70%",
  border: "93 35% 22%",
  ring: "125 43% 51%",
  sidebarBackground: "93 35% 15%",
  sidebarForeground: "0 0% 100%",
};

function pick<K extends string>(env: Record<K, string | undefined>, defaults: Record<K, string>, keys: readonly K[]): Record<K, string> {
  const out = {} as Record<K, string>;
  for (const k of keys) {
    const v = env[k];
    out[k] = (typeof v === "string" && v.trim() !== "" ? v.trim() : defaults[k]) as string;
  }
  return out;
}

const themeKeys = [
  "primary", "primaryForeground", "background", "foreground", "accent", "accentForeground",
  "secondary", "muted", "mutedForeground", "border", "ring", "sidebarBackground", "sidebarForeground",
] as const;

/** HSL values for light mode. PRR mode uses PRR defaults; env overrides when set. */
const light = (() => {
  const env = {
    primary: import.meta.env.VITE_THEME_PRIMARY,
    primaryForeground: import.meta.env.VITE_THEME_PRIMARY_FG,
    background: import.meta.env.VITE_THEME_BACKGROUND,
    foreground: import.meta.env.VITE_THEME_FOREGROUND,
    accent: import.meta.env.VITE_THEME_ACCENT,
    accentForeground: import.meta.env.VITE_THEME_ACCENT_FG,
    secondary: import.meta.env.VITE_THEME_SECONDARY,
    muted: import.meta.env.VITE_THEME_MUTED,
    mutedForeground: import.meta.env.VITE_THEME_MUTED_FG,
    border: import.meta.env.VITE_THEME_BORDER,
    ring: import.meta.env.VITE_THEME_RING,
    sidebarBackground: import.meta.env.VITE_THEME_SIDEBAR_BG,
    sidebarForeground: import.meta.env.VITE_THEME_SIDEBAR_FG,
  };
  return prrMode ? pick(env, prrLight, themeKeys) : env as Record<typeof themeKeys[number], string | undefined>;
})();

/** HSL values for .dark mode. PRR mode uses PRR defaults; env overrides when set. */
const dark = (() => {
  const env = {
    primary: import.meta.env.VITE_THEME_DARK_PRIMARY,
    primaryForeground: import.meta.env.VITE_THEME_DARK_PRIMARY_FG,
    background: import.meta.env.VITE_THEME_DARK_BACKGROUND,
    foreground: import.meta.env.VITE_THEME_DARK_FOREGROUND,
    accent: import.meta.env.VITE_THEME_DARK_ACCENT,
    accentForeground: import.meta.env.VITE_THEME_DARK_ACCENT_FG,
    secondary: import.meta.env.VITE_THEME_DARK_SECONDARY,
    muted: import.meta.env.VITE_THEME_DARK_MUTED,
    mutedForeground: import.meta.env.VITE_THEME_DARK_MUTED_FG,
    border: import.meta.env.VITE_THEME_DARK_BORDER,
    ring: import.meta.env.VITE_THEME_DARK_RING,
    sidebarBackground: import.meta.env.VITE_THEME_DARK_SIDEBAR_BG,
    sidebarForeground: import.meta.env.VITE_THEME_DARK_SIDEBAR_FG,
  };
  return prrMode ? pick(env, prrDark, themeKeys) : env as Record<typeof themeKeys[number], string | undefined>;
})();

function hasAnyTheme(): boolean {
  return prrMode || Object.values(light).some(Boolean) || Object.values(dark).some(Boolean);
}

/** Build a CSS string that overrides :root and .dark with theme vars. */
function buildThemeCSS(): string {
  if (!hasAnyTheme()) return '';

  const rootVars: string[] = [];
  const darkVars: string[] = [];

  if (light.primary) rootVars.push('--primary: ' + light.primary);
  if (light.primaryForeground) rootVars.push('--primary-foreground: ' + light.primaryForeground);
  if (light.background) rootVars.push('--background: ' + light.background);
  if (light.foreground) rootVars.push('--foreground: ' + light.foreground);
  if (light.accent) rootVars.push('--accent: ' + light.accent);
  if (light.accentForeground) rootVars.push('--accent-foreground: ' + light.accentForeground);
  if (light.secondary) rootVars.push('--secondary: ' + light.secondary);
  if (light.muted) rootVars.push('--muted: ' + light.muted);
  if (light.mutedForeground) rootVars.push('--muted-foreground: ' + light.mutedForeground);
  if (light.border) rootVars.push('--border: ' + light.border);
  if (light.ring) rootVars.push('--ring: ' + light.ring);
  if (light.sidebarBackground) rootVars.push('--sidebar-background: ' + light.sidebarBackground);
  if (light.sidebarForeground) rootVars.push('--sidebar-foreground: ' + light.sidebarForeground);
  if (light.primary) rootVars.push('--sidebar-primary: ' + light.primary);
  if (light.accent) rootVars.push('--sidebar-accent: ' + light.accent);

  if (dark.primary) darkVars.push('--primary: ' + dark.primary);
  if (dark.primaryForeground) darkVars.push('--primary-foreground: ' + dark.primaryForeground);
  if (dark.background) darkVars.push('--background: ' + dark.background);
  if (dark.foreground) darkVars.push('--foreground: ' + dark.foreground);
  if (dark.accent) darkVars.push('--accent: ' + dark.accent);
  if (dark.accentForeground) darkVars.push('--accent-foreground: ' + dark.accentForeground);
  if (dark.secondary) darkVars.push('--secondary: ' + dark.secondary);
  if (dark.muted) darkVars.push('--muted: ' + dark.muted);
  if (dark.mutedForeground) darkVars.push('--muted-foreground: ' + dark.mutedForeground);
  if (dark.border) darkVars.push('--border: ' + dark.border);
  if (dark.ring) darkVars.push('--ring: ' + dark.ring);
  if (dark.sidebarBackground) darkVars.push('--sidebar-background: ' + dark.sidebarBackground);
  if (dark.sidebarForeground) darkVars.push('--sidebar-foreground: ' + dark.sidebarForeground);
  if (dark.primary) darkVars.push('--sidebar-primary: ' + dark.primary);
  if (dark.accent) darkVars.push('--sidebar-accent: ' + dark.accent);

  // Visagio palette: map theme primary/accent so components using visagio-teal etc. get the new colors
  if (light.primary) rootVars.push('--visagio-teal: ' + light.primary);
  if (light.sidebarBackground) rootVars.push('--visagio-teal-dark: ' + light.sidebarBackground);
  if (light.accent) rootVars.push('--visagio-aqua: ' + light.accent);
  if (light.secondary) rootVars.push('--visagio-mint: ' + light.secondary);
  if (light.background) rootVars.push('--visagio-off-white: ' + light.background);
  if (light.foreground) rootVars.push('--visagio-dark: ' + light.foreground);
  if (dark.primary) darkVars.push('--visagio-teal: ' + dark.primary);
  if (dark.sidebarBackground) darkVars.push('--visagio-teal-dark: ' + dark.sidebarBackground);
  if (dark.accent) darkVars.push('--visagio-aqua: ' + dark.accent);
  if (dark.secondary) darkVars.push('--visagio-mint: ' + dark.secondary);
  if (dark.background) darkVars.push('--visagio-off-white: ' + dark.background);
  if (dark.foreground) darkVars.push('--visagio-dark: ' + dark.foreground);

  let css = '';
  if (rootVars.length) css += `:root { ${rootVars.join('; ')} }\n`;
  if (darkVars.length) css += `.dark { ${darkVars.join('; ')} }\n`;
  return css;
}

/** Inject theme overrides into the document if any VITE_THEME_* are set. */
export function injectTheme(): void {
  const css = buildThemeCSS();
  if (!css) return;
  const style = document.createElement('style');
  style.id = 'theme-overrides';
  style.textContent = css;
  document.head.appendChild(style);
}
